import React, { useState } from "react";
import { useTrackingLinks } from "@/hooks/use-offers";
import { Card } from "@/components/Card";
import { Copy, Check, Link2, ChevronDown, ChevronRight, ExternalLink, Users } from "lucide-react";

interface Props {
  offerId: string;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      onClick={handleCopy}
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-all ${
        copied
          ? "bg-green-50 text-green-700 border border-green-200"
          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
      }`}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function LinkRow({ label, url, sublabel }: { label: string; url: string; sublabel?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-700">{label}</span>
          {sublabel && <span className="text-xs text-gray-400">{sublabel}</span>}
        </div>
        <div className="mt-1 font-mono text-xs text-gray-500 break-all bg-gray-50 rounded-md px-3 py-2 border border-gray-100">
          {url}
        </div>
      </div>
      <div className="shrink-0 pt-5">
        <CopyButton text={url} />
      </div>
    </div>
  );
}

interface AffiliateLinks {
  affiliateId: number;
  affiliateName: string;
  status: string;
  approvalStatus: string;
  links: {
    default: {
      name: string;
      clickUrl: string;
      clickUrlWithSubs: string;
      impressionUrl: string;
    };
    offerUrls: Array<{
      name: string;
      offerUrlId: number;
      clickUrl: string;
      clickUrlWithSubs: string;
    }>;
  };
}

function AffiliateSection({ affiliate }: { affiliate: AffiliateLinks }) {
  const [expanded, setExpanded] = useState(true);

  const isActive = affiliate.status === "active" && affiliate.approvalStatus === "approved";

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <div className="text-left">
            <span className="text-sm font-medium text-gray-900">{affiliate.affiliateName}</span>
            <span className="text-xs text-gray-400 ml-2">ID: {affiliate.affiliateId}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isActive && (
            <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full border border-yellow-200">
              {affiliate.approvalStatus !== "approved" ? affiliate.approvalStatus : affiliate.status}
            </span>
          )}
          {isActive && (
            <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full border border-green-200">
              Active
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 bg-white border-t border-gray-100 space-y-1">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-2 mb-1">Click URLs</div>

          <LinkRow
            label="Default Click URL"
            url={affiliate.links.default.clickUrl}
          />

          <LinkRow
            label="Click URL with Sub IDs"
            url={affiliate.links.default.clickUrlWithSubs}
            sublabel="Replace {sub1}-{sub5} with your values"
          />

          {affiliate.links.offerUrls.length > 0 && (
            <>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-4 mb-1">
                Offer URL Variants
              </div>
              {affiliate.links.offerUrls.map((ou) => (
                <LinkRow
                  key={ou.offerUrlId}
                  label={ou.name}
                  url={ou.clickUrl}
                  sublabel={`URL ID: ${ou.offerUrlId}`}
                />
              ))}
            </>
          )}

          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-4 mb-1">
            Impression Pixel
          </div>
          <LinkRow
            label="Impression URL"
            url={affiliate.links.default.impressionUrl}
          />
        </div>
      )}
    </div>
  );
}

export function TrackingLinksTab({ offerId }: Props) {
  const { data, isLoading } = useTrackingLinks(offerId);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 bg-gray-100 rounded-xl" />
        <div className="h-32 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  const trackingData = data?.data;
  const links: AffiliateLinks[] = trackingData?.trackingLinks ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-gray-900">Tracking Links</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Click URLs for each assigned affiliate. Share these links so affiliates can start driving traffic.
        </p>
      </div>

      {/* Postback Template */}
      {trackingData?.postbackTemplate && (
        <Card title="Conversion Postback URL">
          <p className="text-xs text-gray-500 mb-2">
            Give this to your advertiser. They fire this URL when a conversion happens.
          </p>
          <div className="flex items-start gap-3">
            <div className="flex-1 font-mono text-xs text-gray-600 break-all bg-gray-50 rounded-md px-3 py-2 border border-gray-100">
              {trackingData.postbackTemplate}
            </div>
            <CopyButton text={trackingData.postbackTemplate} />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Replace <code className="bg-gray-100 px-1 rounded">{"{transaction_id}"}</code> with the actual value passed in the click URL,
            and <code className="bg-gray-100 px-1 rounded">{"{adv_event_id}"}</code> with the advertiser's event ID.
          </p>
        </Card>
      )}

      {/* Per-affiliate tracking links */}
      {links.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300 rounded-xl">
          <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No tracking links available</p>
          <p className="text-xs text-gray-400 mt-1">
            Assign affiliates to this offer first to generate tracking links
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((affiliate) => (
            <AffiliateSection key={affiliate.affiliateId} affiliate={affiliate} />
          ))}
        </div>
      )}
    </div>
  );
}
